/**
 * 找回密码 Modal
 *
 * 三步流程:
 *   1. 输入手机号 → 获取验证码
 *   2. 输入验证码
 *   3. 输入新密码 + 确认
 *   4. 成功 → 提示用新密码登录
 *
 * 不需要登录(未登录状态)
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone, KeyRound, Lock, Eye, EyeOff, ShieldCheck, X,
  Loader2, Check, ArrowRight, MessageSquare
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../lib/api.js';

const STEPS = {
  PHONE: 'phone',
  CODE: 'code',
  PASSWORD: 'password',
  DONE: 'done',
};

export default function ForgotPasswordModal({ open, onClose, onSwitchToLogin }) {
  const [step, setStep] = useState(STEPS.PHONE);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [devCode, setDevCode] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  useEffect(() => {
    if (!open) {
      setStep(STEPS.PHONE);
      setPhone('');
      setCode('');
      setNewPwd('');
      setConfirmPwd('');
      setCountdown(0);
      setDevCode('');
    }
  }, [open]);

  const phoneValid = /^1[3-9]\d{9}$/.test(phone);

  const handleSendCode = async () => {
    if (!phoneValid) {
      toast.error('请输入正确的手机号');
      return;
    }
    setLoading(true);
    try {
      const res = await api.sendCode(phone);
      if (res.ok) {
        toast.success('验证码已发送');
        setCountdown(60);
        if (res._devCode) setDevCode(res._devCode);
        setStep(STEPS.CODE);
      } else {
        toast.error('发送失败,请重试');
      }
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  const handleVerifyCode = () => {
    if (code.length !== 6) {
      toast.error('请输入 6 位验证码');
      return;
    }
    setStep(STEPS.PASSWORD);
  };

  const handleResetPassword = async () => {
    if (newPwd.length < 6) {
      toast.error('密码至少 6 位');
      return;
    }
    if (newPwd.length > 64) {
      toast.error('密码不能超过 64 位');
      return;
    }
    if (/^\d+$/.test(newPwd)) {
      toast.error('密码不能全是数字');
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error('两次密码不一致');
      return;
    }
    setLoading(true);
    try {
      const res = await api.forgotPassword(phone, code, newPwd);
      if (res.ok) {
        setStep(STEPS.DONE);
        toast.success('密码已重设!');
      } else {
        toast.error(res.error || '重设失败');
      }
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  const handleSwitchToLogin = () => {
    if (onSwitchToLogin) {
      onSwitchToLogin(phone);
    }
    onClose();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 30, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={e => e.stopPropagation()}
          className="w-full sm:max-w-md glass-strong rounded-2xl p-6 sm:p-8 relative"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-ink-3 hover:text-ink-0"
          >
            <X size={20} />
          </button>

          <div className="text-center mb-5">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-cyan to-brand-purple mb-3">
              <KeyRound size={26} className="text-white" />
            </div>
            <h2 className="text-xl font-bold">找回密码</h2>
            <p className="text-xs text-ink-3 mt-1">
              {step === STEPS.DONE ? '重设成功' : '通过手机验证码重设密码'}
            </p>
          </div>

          {/* 步骤指示 */}
          {step !== STEPS.DONE && (
            <div className="flex items-center justify-center gap-1 mb-5">
              {['手机号', '验证码', '新密码'].map((label, i) => {
                const active =
                  (i === 0 && step === STEPS.PHONE) ||
                  (i === 1 && step === STEPS.CODE) ||
                  (i === 2 && step === STEPS.PASSWORD);
                const done = (i === 0 && [STEPS.CODE, STEPS.PASSWORD].includes(step)) ||
                            (i === 1 && step === STEPS.PASSWORD);
                return (
                  <div key={label} className="flex items-center gap-1">
                    <div className={clsx(
                      'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition',
                      done ? 'bg-brand-gold/30 text-brand-gold' :
                      active ? 'bg-brand-cyan/30 text-brand-cyan' :
                      'bg-white/5 text-ink-3'
                    )}>
                      {done ? <Check size={12} /> : i + 1}
                    </div>
                    <span className={clsx(
                      'text-[10px]',
                      active ? 'text-brand-cyan font-semibold' :
                      done ? 'text-brand-gold' : 'text-ink-3'
                    )}>{label}</span>
                    {i < 2 && <ArrowRight size={10} className="text-ink-3 mx-1" />}
                  </div>
                );
              })}
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* Step 1: 手机号 */}
            {step === STEPS.PHONE && (
              <motion.div
                key="phone"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-4"
              >
                <div>
                  <label className="text-xs text-ink-3 mb-1.5 block">注册手机号</label>
                  <div className="flex items-center gap-2 bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-brand-purple/40 transition">
                    <Phone size={16} className="text-ink-3" />
                    <span className="text-ink-2 text-sm">+86</span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      placeholder="13800000000"
                      maxLength={11}
                      autoFocus
                      className="flex-1 bg-transparent outline-none text-base"
                    />
                  </div>
                </div>
                <button
                  onClick={handleSendCode}
                  disabled={!phoneValid || loading}
                  className={clsx(
                    'w-full py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2',
                    phoneValid && !loading ? 'btn-primary' : 'bg-white/5 text-ink-3 cursor-not-allowed'
                  )}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <MessageSquare size={16} />}
                  获取验证码
                </button>
              </motion.div>
            )}

            {/* Step 2: 验证码 */}
            {step === STEPS.CODE && (
              <motion.div
                key="code"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-4"
              >
                <div className="rounded-lg bg-brand-cyan/5 border border-brand-cyan/20 p-2.5 text-xs text-center text-ink-2">
                  验证码已发送至 <span className="text-brand-cyan font-semibold">
                    {phone.slice(0, 3)}****{phone.slice(-4)}
                  </span>
                </div>
                <div>
                  <label className="text-xs text-ink-3 mb-1.5 block">6 位验证码</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    autoFocus
                    className="w-full bg-bg-card border border-white/10 rounded-lg px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] outline-none focus:border-brand-purple/40"
                  />
                </div>
                {devCode && (
                  <div className="text-[10px] text-center text-ink-3 bg-brand-gold/5 border border-brand-gold/20 rounded p-2">
                    🛠 开发模式验证码:<span className="font-mono text-brand-gold ml-1">{devCode}</span>
                  </div>
                )}
                <button
                  onClick={handleVerifyCode}
                  disabled={code.length !== 6}
                  className={clsx(
                    'w-full py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2',
                    code.length === 6 ? 'btn-primary' : 'bg-white/5 text-ink-3 cursor-not-allowed'
                  )}
                >
                  下一步
                  <ArrowRight size={14} />
                </button>
                <div className="flex justify-between text-xs">
                  <button
                    onClick={() => { setStep(STEPS.PHONE); setCode(''); }}
                    className="text-ink-3 hover:text-ink-0"
                  >
                    ← 换手机号
                  </button>
                  <button
                    onClick={handleSendCode}
                    disabled={countdown > 0}
                    className={clsx(countdown > 0 ? 'text-ink-3' : 'text-brand-cyan')}
                  >
                    {countdown > 0 ? `${countdown}秒后重发` : '重发验证码'}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: 新密码 */}
            {step === STEPS.PASSWORD && (
              <motion.div
                key="pwd"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-3"
              >
                <div>
                  <label className="text-xs text-ink-3 mb-1.5 block">新密码(6-64 位,不能全是数字)</label>
                  <div className="flex items-center gap-2 bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-brand-purple/40 transition">
                    <Lock size={14} className="text-ink-3" />
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={newPwd}
                      onChange={e => setNewPwd(e.target.value)}
                      placeholder="建议字母+数字组合"
                      maxLength={64}
                      autoFocus
                      className="flex-1 bg-transparent outline-none"
                    />
                    <button onClick={() => setShowPwd(!showPwd)} className="text-ink-3">
                      {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-ink-3 mb-1.5 block">确认新密码</label>
                  <div className="flex items-center gap-2 bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-brand-purple/40 transition">
                    <Lock size={14} className="text-ink-3" />
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={confirmPwd}
                      onChange={e => setConfirmPwd(e.target.value)}
                      placeholder="再输一次"
                      maxLength={64}
                      className="flex-1 bg-transparent outline-none"
                    />
                  </div>
                </div>
                {newPwd && <PwdStrength value={newPwd} />}
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button
                    onClick={() => { setStep(STEPS.CODE); setNewPwd(''); setConfirmPwd(''); }}
                    className="btn-secondary"
                  >
                    ← 上一步
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={loading || newPwd.length < 6 || newPwd !== confirmPwd}
                    className={clsx(
                      'py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2',
                      !loading && newPwd.length >= 6 && newPwd === confirmPwd
                        ? 'btn-primary' : 'bg-white/5 text-ink-3 cursor-not-allowed'
                    )}
                  >
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                    重设密码
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: 完成 */}
            {step === STEPS.DONE && (
              <motion.div
                key="done"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="text-center space-y-4"
              >
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-brand-gold to-amber-500 flex items-center justify-center">
                  <Check size={40} className="text-bg-base" />
                </div>
                <div>
                  <div className="text-lg font-bold text-brand-gold">密码已重设!</div>
                  <div className="text-xs text-ink-2 mt-1">
                    请用新密码登录
                  </div>
                </div>
                <button
                  onClick={handleSwitchToLogin}
                  className="btn-primary w-full"
                >
                  去登录 →
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function PwdStrength({ value }) {
  let level = 0;
  if (value.length >= 8) level++;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) level++;
  if (/\d/.test(value)) level++;
  if (/[^A-Za-z0-9]/.test(value)) level++;

  const labels = ['弱', '中弱', '中', '中强', '强'];
  const colors = ['text-accent-danger', 'text-orange-400', 'text-amber-400', 'text-brand-cyan', 'text-brand-gold'];
  return (
    <div className="flex items-center gap-2 text-xs">
      <div className="flex-1 grid grid-cols-4 gap-1">
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            className={clsx(
              'h-1 rounded-full transition',
              i < level ? 'bg-current' : 'bg-white/10',
              colors[level - 1] || 'text-white/20'
            )}
          />
        ))}
      </div>
      <span className={clsx('font-semibold', colors[level - 1] || 'text-ink-3')}>
        {labels[level - 1] || '太弱'}
      </span>
    </div>
  );
}
